import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FileText, ImageIcon, Loader2, Plus, Save, Trash2, Upload, X, Download, Layers, PenLine, Eye, EyeOff, Library } from 'lucide-react';
import type { FieldVisibility } from '../components/new-components/PropertyCard';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { offeringsService, Offering } from '../services/offerings';
import api from '../services/api';
import BankAccountSelector from '../components/BankAccountSelector';

interface DealDocument {
  id: string;
  deal_id: string;
  kind: string;
  title: string;
  filename: string;
  file_size_bytes?: number;
  mime_type?: string;
  download_url: string;
  created_at: string;
  requires_esign?: boolean;
  visible_to?: 'public' | 'investors' | 'holders';
}

interface LibraryDoc {
  id: string;
  title: string;
  folder: string;
  category: string;
  s3_key: string;
  file_size_bytes?: number;
  created_at: string;
}

type CreateFormState = {
  type: Offering['type'];
  title: string;
  summary: string;
  status: Offering['status'];
  raiseTarget: string;
  minInvest: string;
  slug: string;
  location: string;
  featured: boolean;
  increaseAccountId: string;
};

type EditFormState = {
  title: string;
  summary: string;
  status: Offering['status'];
  raiseTarget: string;
  minInvest: string;
  imageUrl: string;
  location: string;
  featured: boolean;
  investEnabled: boolean;
  increaseAccountId: string;
  fieldVisibility: FieldVisibility;
};

const DEFAULT_CREATE: CreateFormState = {
  type: 'fund',
  title: '',
  summary: '',
  status: 'draft',
  raiseTarget: '',
  minInvest: '',
  slug: '',
  location: '',
  featured: false,
  increaseAccountId: '',
};

function toNumberOrUndefined(value: string): number | undefined {
  const trimmed = String(value || '').trim();
  if (!trimmed) return undefined;
  const num = Number(trimmed);
  return Number.isFinite(num) ? num : undefined;
}

const AdminOfferings: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [offerings, setOfferings] = useState<Offering[]>([]);
  const [createForm, setCreateForm] = useState<CreateFormState>(DEFAULT_CREATE);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EditFormState | null>(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);
  const [dealDocs, setDealDocs] = useState<DealDocument[]>([]);
  const [docUploading, setDocUploading] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [libraryDocs, setLibraryDocs] = useState<LibraryDoc[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(false);

  const sortedOfferings = useMemo(() => {
    return [...offerings].sort((a, b) => {
      const aTs = new Date(a.createdAt).getTime();
      const bTs = new Date(b.createdAt).getTime();
      return bTs - aTs;
    });
  }, [offerings]);

  const load = async () => {
    try {
      setLoading(true);
      const data = await offeringsService.adminGetAll();
      setOfferings(data);
    } catch (error) {
      console.error('[AdminOfferings] load failed', error);
      toast.error('Failed to load offerings');
      setOfferings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startEdit = (offering: Offering) => {
    setEditingId(offering.id);
    setEditForm({
      title: offering.title || '',
      summary: offering.summary || '',
      status: offering.status,
      raiseTarget: offering.raiseTarget != null ? String(offering.raiseTarget) : '',
      minInvest: offering.minInvest != null ? String(offering.minInvest) : '',
      imageUrl: String(offering.branding?.imageUrl || ''),
      location: String(offering.branding?.location || ''),
      featured: Boolean(offering.settings?.featured),
      investEnabled: Boolean(offering.settings?.investEnabled),
      increaseAccountId: offering.increaseAccountId || '',
      fieldVisibility: (offering.settings?.fieldVisibility as FieldVisibility) || {},
    });
    // Load documents for this offering
    loadDealDocs(offering.id);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(null);
    setDealDocs([]);
  };

  const handleCoverUpload = async (id: string, file: File) => {
    try {
      setUploadingCover(true);
      const result = await offeringsService.adminUploadCover(id, file);
      toast.success('Cover image uploaded');
      setEditForm((s) => (s ? { ...s, imageUrl: result.imageUrl } : s));
      await load();
    } catch (error: any) {
      console.error('[AdminOfferings] cover upload failed', error);
      toast.error(error?.message || 'Failed to upload cover image');
    } finally {
      setUploadingCover(false);
      if (coverInputRef.current) coverInputRef.current.value = '';
    }
  };

  const handleCoverDelete = async (id: string) => {
    const ok = window.confirm('Remove cover image?');
    if (!ok) return;
    try {
      setUploadingCover(true);
      await offeringsService.adminDeleteCover(id);
      toast.success('Cover image removed');
      setEditForm((s) => (s ? { ...s, imageUrl: '' } : s));
      await load();
    } catch (error: any) {
      console.error('[AdminOfferings] cover delete failed', error);
      toast.error(error?.message || 'Failed to remove cover image');
    } finally {
      setUploadingCover(false);
    }
  };

  const loadDealDocs = async (dealId: string) => {
    try {
      const res: any = await api.get(`/admin/deals/${dealId}/documents`);
      // api.get() returns response.data directly: { success, data: [...] }
      setDealDocs(res?.data || []);
    } catch { setDealDocs([]); }
  };

  const handleDocUpload = async (dealId: string, files: FileList) => {
    setDocUploading(true);
    try {
      const fd = new FormData();
      for (let i = 0; i < files.length; i++) fd.append('documents', files[i]);
      await api.post(`/admin/deals/${dealId}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${files.length} document(s) uploaded`);
      await loadDealDocs(dealId);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Document upload failed');
    } finally {
      setDocUploading(false);
      if (docInputRef.current) docInputRef.current.value = '';
    }
  };

  const handleDocDelete = async (dealId: string, docId: string) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await api.delete(`/admin/deals/${dealId}/documents/${docId}`);
      toast.success('Document deleted');
      setDealDocs(prev => prev.filter(d => d.id !== docId));
    } catch { toast.error('Failed to delete document'); }
  };

  const handleEsignToggle = async (dealId: string, docId: string, currentValue: boolean) => {
    try {
      await api.patch(`/admin/deals/${dealId}/documents/${docId}`, { requires_esign: !currentValue });
      setDealDocs(prev => prev.map(d => d.id === docId ? { ...d, requires_esign: !currentValue } : d));
      toast.success(!currentValue ? 'E-signature required' : 'E-signature removed');
    } catch { toast.error('Failed to update e-signature setting'); }
  };

  const handleVisibilityChange = async (dealId: string, docId: string, visibleTo: string) => {
    try {
      await api.patch(`/admin/deals/${dealId}/documents/${docId}`, { visible_to: visibleTo });
      setDealDocs(prev => prev.map(d => d.id === docId ? { ...d, visible_to: visibleTo as DealDocument['visible_to'] } : d));
      const labels: Record<string, string> = { public: 'Public', investors: 'During Investment', holders: 'Holders Only' };
      toast.success(`Visibility set to ${labels[visibleTo] || visibleTo}`);
    } catch { toast.error('Failed to update visibility'); }
  };

  const openLibrary = async () => {
    setShowLibrary(true);
    setLibraryLoading(true);
    try {
      const res: any = await api.get('/admin/documents/library');
      setLibraryDocs(res?.data || []);
    } catch { toast.error('Failed to load document library'); setLibraryDocs([]); }
    finally { setLibraryLoading(false); }
  };

  const handleAttachDoc = async (dealId: string, libraryDocId: string) => {
    try {
      await api.post(`/admin/deals/${dealId}/documents/attach`, { documentId: libraryDocId });
      toast.success('Document attached to offering');
      await loadDealDocs(dealId);
      setShowLibrary(false);
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to attach document';
      toast.error(msg);
    }
  };

  const fmtBytes = (b?: number) => {
    if (!b) return '';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  };

  const saveEdit = async (id: string) => {
    if (!editForm) return;

    try {
      setSaving(true);
      await offeringsService.adminUpdate(id, {
        title: editForm.title,
        summary: editForm.summary,
        status: editForm.status,
        raiseTarget: toNumberOrUndefined(editForm.raiseTarget),
        minInvest: toNumberOrUndefined(editForm.minInvest),
        branding: {
          location: editForm.location || undefined,
        },
        settings: {
          featured: editForm.featured,
          investEnabled: editForm.investEnabled,
          fieldVisibility: editForm.fieldVisibility,
        },
        increaseAccountId: editForm.increaseAccountId || undefined,
      });

      toast.success('Offering updated');
      cancelEdit();
      await load();
    } catch (error: any) {
      console.error('[AdminOfferings] save failed', error);
      toast.error(error?.message || 'Failed to update offering');
    } finally {
      setSaving(false);
    }
  };

  const archiveOffering = async (id: string) => {
    const ok = window.confirm('Archive this offering?');
    if (!ok) return;

    try {
      setSaving(true);
      await offeringsService.adminDelete(id);
      toast.success('Offering archived');
      await load();
    } catch (error: any) {
      console.error('[AdminOfferings] archive failed', error);
      toast.error(error?.message || 'Failed to archive offering');
    } finally {
      setSaving(false);
    }
  };

  const createOffering = async () => {
    const title = createForm.title.trim();
    if (!title) {
      toast.error('Title is required');
      return;
    }

    try {
      setSaving(true);
      await offeringsService.adminCreate({
        type: createForm.type,
        title,
        summary: createForm.summary.trim() || undefined,
        status: createForm.status,
        raiseTarget: toNumberOrUndefined(createForm.raiseTarget),
        minInvest: toNumberOrUndefined(createForm.minInvest),
        slug: createForm.slug.trim() || undefined,
        branding: {
          location: createForm.location.trim() || undefined,
        },
        settings: {
          featured: createForm.featured,
        },
        increaseAccountId: createForm.increaseAccountId || undefined,
      });
      toast.success('Offering created');
      setCreateForm(DEFAULT_CREATE);
      await load();
    } catch (error: any) {
      console.error('[AdminOfferings] create failed', error);
      toast.error(error?.message || 'Failed to create offering');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5 sm:space-y-6 min-w-0">
      {/* Create */}
      <div className="bg-white border border-border rounded-xl p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h2 className="text-gray-900 font-semibold">Create Offering</h2>
          <button
            onClick={createOffering}
            disabled={saving}
            className="inline-flex w-full sm:w-auto items-center justify-center gap-2 px-3 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-500 disabled:opacity-50"
          >
            <Plus size={16} />
            Create
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-muted-foreground">Type *</label>
            <select
              value={createForm.type}
              onChange={(e) => setCreateForm((s) => ({ ...s, type: e.target.value as Offering['type'] }))}
              className="mt-1 w-full px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-gray-900"
            >
              <option value="fund">Fund</option>
              <option value="property">Property</option>
              <option value="development">Development</option>
              <option value="debt">Debt</option>
              <option value="license">License</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="text-xs text-muted-foreground">Title *</label>
            <input
              value={createForm.title}
              onChange={(e) => setCreateForm((s) => ({ ...s, title: e.target.value }))}
              placeholder="e.g., Hive Fund"
              className="mt-1 w-full px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-gray-900 placeholder:text-gray-400"
            />
          </div>

          <div>
            <label className="text-xs text-muted-foreground">Status</label>
            <select
              value={createForm.status}
              onChange={(e) => setCreateForm((s) => ({ ...s, status: e.target.value as Offering['status'] }))}
              className="mt-1 w-full px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-gray-900"
            >
              <option value="draft">draft</option>
              <option value="open">open</option>
              <option value="closed">closed</option>
              <option value="exited">exited</option>
              <option value="archived">archived</option>
            </select>
          </div>

          <div>
            <label className="text-xs text-muted-foreground">Raise Target ($)</label>
            <input
              value={createForm.raiseTarget}
              onChange={(e) => setCreateForm((s) => ({ ...s, raiseTarget: e.target.value }))}
              inputMode="decimal"
              placeholder="500000"
              className="mt-1 w-full px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-gray-900 placeholder:text-gray-400"
            />
          </div>

          <div>
            <label className="text-xs text-muted-foreground">Min Investment ($)</label>
            <input
              value={createForm.minInvest}
              onChange={(e) => setCreateForm((s) => ({ ...s, minInvest: e.target.value }))}
              inputMode="decimal"
              placeholder="100"
              className="mt-1 w-full px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-gray-900 placeholder:text-gray-400"
            />
          </div>

          <div>
            <label className="text-xs text-muted-foreground">Location</label>
            <input
              value={createForm.location}
              onChange={(e) => setCreateForm((s) => ({ ...s, location: e.target.value }))}
              placeholder="City, ST"
              className="mt-1 w-full px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-gray-900 placeholder:text-gray-400"
            />
          </div>

          <div>
            <label className="text-xs text-muted-foreground">Slug (optional)</label>
            <input
              value={createForm.slug}
              onChange={(e) => setCreateForm((s) => ({ ...s, slug: e.target.value }))}
              placeholder="auto-generated from title"
              className="mt-1 w-full px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-gray-900 placeholder:text-gray-400"
            />
          </div>

          <BankAccountSelector
            value={createForm.increaseAccountId}
            onChange={(id) => setCreateForm((s) => ({ ...s, increaseAccountId: id }))}
            label="Bank Account"
            autoDefault={true}
          />

          <div className="flex items-center gap-2 mt-6">
            <input
              id="create-featured"
              type="checkbox"
              checked={createForm.featured}
              onChange={(e) => setCreateForm((s) => ({ ...s, featured: e.target.checked }))}
              className="h-4 w-4"
            />
            <label htmlFor="create-featured" className="text-sm text-gray-700">Featured</label>
          </div>

          <div className="md:col-span-4">
            <label className="text-xs text-muted-foreground">Summary</label>
            <textarea
              value={createForm.summary}
              onChange={(e) => setCreateForm((s) => ({ ...s, summary: e.target.value }))}
              placeholder="Short description shown on Invest cards"
              rows={3}
              className="mt-1 w-full px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-gray-900 placeholder:text-gray-400"
            />
          </div>
        </div>
      </div>

      {/* List */}
      <div className="bg-white border border-border rounded-xl p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h2 className="text-gray-900 font-semibold">Offerings</h2>
          <button
            onClick={load}
            disabled={loading || saving}
            className="w-full sm:w-auto px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
          >
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-gray-500">
            <Loader2 className="animate-spin" size={18} />
            Loading...
          </div>
        ) : sortedOfferings.length === 0 ? (
          <div className="text-muted-foreground text-sm">No offerings yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="text-muted-foreground border-b border-gray-200">
                  <th className="text-left py-2 pr-4 font-medium">Title</th>
                  <th className="text-left py-2 pr-4 font-medium">Type</th>
                  <th className="text-left py-2 pr-4 font-medium">Status</th>
                  <th className="text-left py-2 pr-4 font-medium">Slug</th>
                  <th className="text-left py-2 pr-4 font-medium">Created</th>
                  <th className="text-right py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedOfferings.map((o) => {
                  const isEditing = editingId === o.id;
                  return (
                    <React.Fragment key={o.id}>
                      <tr className="border-b border-gray-200 text-gray-700 align-top">
                        <td className="py-3 pr-4">
                          <div className="font-medium text-gray-900">{o.title}</div>
                          {o.summary ? (
                            <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{o.summary}</div>
                          ) : null}
                        </td>
                        <td className="py-3 pr-4">{o.type}</td>
                        <td className="py-3 pr-4">{o.status}</td>
                        <td className="py-3 pr-4 text-muted-foreground">{o.slug}</td>
                        <td className="py-3 pr-4 text-muted-foreground">{new Date(o.createdAt).toLocaleString()}</td>
                        <td className="py-3 text-right whitespace-nowrap">
                          {isEditing ? (
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => saveEdit(o.id)}
                                disabled={saving}
                                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-500 disabled:opacity-50"
                              >
                                <Save size={16} />
                                Save
                              </button>
                              <button
                                onClick={cancelEdit}
                                disabled={saving}
                                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                              >
                                <X size={16} />
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => startEdit(o)}
                                className="px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-gray-700 hover:bg-gray-100"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => archiveOffering(o.id)}
                                disabled={saving}
                                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-red-600/20 text-red-300 hover:bg-red-600/30 disabled:opacity-50"
                              >
                                <Trash2 size={16} />
                                Archive
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>

                      {isEditing && editForm ? (
                        <tr className="border-b border-gray-200">
                          <td colSpan={6} className="py-4">
                            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                              <div className="md:col-span-3">
                                <label className="text-xs text-muted-foreground">Title</label>
                                <input
                                  value={editForm.title}
                                  onChange={(e) => setEditForm((s) => (s ? { ...s, title: e.target.value } : s))}
                                  className="mt-1 w-full px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-gray-900"
                                />
                              </div>

                              <div>
                                <label className="text-xs text-muted-foreground">Status</label>
                                <select
                                  value={editForm.status}
                                  onChange={(e) =>
                                    setEditForm((s) =>
                                      s ? { ...s, status: e.target.value as Offering['status'] } : s
                                    )
                                  }
                                  className="mt-1 w-full px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-gray-900"
                                >
                                  <option value="draft">draft</option>
                                  <option value="open">open</option>
                                  <option value="closed">closed</option>
                                  <option value="exited">exited</option>
                                  <option value="archived">archived</option>
                                </select>
                              </div>

                              <div>
                                <label className="text-xs text-muted-foreground">Raise Target ($)</label>
                                <input
                                  value={editForm.raiseTarget}
                                  onChange={(e) => setEditForm((s) => (s ? { ...s, raiseTarget: e.target.value } : s))}
                                  inputMode="decimal"
                                  className="mt-1 w-full px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-gray-900"
                                />
                              </div>

                              <div>
                                <label className="text-xs text-muted-foreground">Min Invest ($)</label>
                                <input
                                  value={editForm.minInvest}
                                  onChange={(e) => setEditForm((s) => (s ? { ...s, minInvest: e.target.value } : s))}
                                  inputMode="decimal"
                                  className="mt-1 w-full px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-gray-900"
                                />
                              </div>

                              <div className="md:col-span-3">
                                <label className="text-xs text-muted-foreground">Cover Image</label>
                                <input
                                  ref={coverInputRef}
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleCoverUpload(o.id, file);
                                  }}
                                />
                                {editForm.imageUrl ? (
                                  <div className="mt-1 flex items-center gap-3">
                                    <img
                                      src={editForm.imageUrl}
                                      alt="Cover"
                                      className="h-20 w-32 object-cover rounded-lg border border-gray-200"
                                    />
                                    <div className="flex flex-col gap-1">
                                      <button
                                        type="button"
                                        onClick={() => coverInputRef.current?.click()}
                                        disabled={uploadingCover}
                                        className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
                                      >
                                        <Upload size={12} /> Replace
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleCoverDelete(o.id)}
                                        disabled={uploadingCover}
                                        className="inline-flex items-center gap-1 text-xs text-red-500 hover:text-red-700"
                                      >
                                        <Trash2 size={12} /> Remove
                                      </button>
                                    </div>
                                    {uploadingCover && <Loader2 className="animate-spin" size={16} />}
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => coverInputRef.current?.click()}
                                    disabled={uploadingCover}
                                    className="mt-1 flex items-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700 w-full"
                                  >
                                    {uploadingCover ? (
                                      <Loader2 className="animate-spin" size={16} />
                                    ) : (
                                      <ImageIcon size={16} />
                                    )}
                                    <span className="text-sm">Upload cover image</span>
                                  </button>
                                )}
                              </div>

                              <div className="md:col-span-2">
                                <label className="text-xs text-muted-foreground">Location</label>
                                <input
                                  value={editForm.location}
                                  onChange={(e) => setEditForm((s) => (s ? { ...s, location: e.target.value } : s))}
                                  placeholder="City, ST"
                                  className="mt-1 w-full px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-gray-900"
                                />
                              </div>

                              <div className="flex items-center gap-2 md:col-span-1 mt-6">
                                <input
                                  id={`featured-${o.id}`}
                                  type="checkbox"
                                  checked={editForm.featured}
                                  onChange={(e) => setEditForm((s) => (s ? { ...s, featured: e.target.checked } : s))}
                                  className="h-4 w-4"
                                />
                                <label htmlFor={`featured-${o.id}`} className="text-sm text-gray-700">
                                  Featured
                                </label>
                              </div>

                              <div className="flex items-center gap-2 md:col-span-1 mt-6">
                                <input
                                  id={`invest-enabled-${o.id}`}
                                  type="checkbox"
                                  checked={editForm.investEnabled}
                                  onChange={(e) => setEditForm((s) => (s ? { ...s, investEnabled: e.target.checked } : s))}
                                  className="h-4 w-4 accent-emerald-600"
                                />
                                <label htmlFor={`invest-enabled-${o.id}`} className="text-sm text-gray-700">
                                  Invest Now Live
                                </label>
                              </div>

                              <BankAccountSelector
                                value={editForm.increaseAccountId}
                                onChange={(id) => setEditForm((s) => (s ? { ...s, increaseAccountId: id } : s))}
                                label="Bank Account"
                                className="md:col-span-2"
                                autoDefault={false}
                              />

                              <div className="md:col-span-6">
                                <label className="text-xs text-muted-foreground">Summary</label>
                                <textarea
                                  rows={3}
                                  value={editForm.summary}
                                  onChange={(e) => setEditForm((s) => (s ? { ...s, summary: e.target.value } : s))}
                                  className="mt-1 w-full px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-gray-900"
                                />
                              </div>

                              {/* ── Card Display Settings ── */}
                              <div className="md:col-span-6 border-t pt-4 mt-2">
                                <label className="text-xs font-semibold text-muted-foreground flex items-center gap-1 mb-3">
                                  <Eye className="w-3.5 h-3.5" /> Card Display Settings
                                </label>
                                <p className="text-xs text-muted-foreground mb-3">Toggle which fields appear on this offering's investor card.</p>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                  {([
                                    { key: 'show_target_return' as const, label: 'Target Return' },
                                    { key: 'show_share_price' as const, label: 'Share Price' },
                                    { key: 'show_investor_count' as const, label: 'Investor Count' },
                                    { key: 'show_property_value' as const, label: 'Property Value' },
                                    { key: 'show_funding_progress' as const, label: 'Funding Status Bar' },
                                  ]).map(({ key, label }) => {
                                    const checked = editForm.fieldVisibility[key] !== false;
                                    return (
                                      <label key={key} className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 border border-gray-200 text-sm cursor-pointer hover:bg-gray-100">
                                        <input
                                          type="checkbox"
                                          checked={checked}
                                          onChange={() => setEditForm(s => s ? {
                                            ...s,
                                            fieldVisibility: { ...s.fieldVisibility, [key]: !checked },
                                          } : s)}
                                          className="h-4 w-4 accent-emerald-600"
                                        />
                                        <span className="flex items-center gap-1.5">
                                          {checked ? <Eye className="w-3 h-3 text-emerald-600" /> : <EyeOff className="w-3 h-3 text-gray-400" />}
                                          {label}
                                        </span>
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* ── Tags ── */}
                              <div className="md:col-span-6 border-t pt-4 mt-2">
                                <OfferingTagEditor
                                  offeringId={o.id}
                                  initialTags={o.tags || []}
                                  onTagsUpdated={(tags) => {
                                    setOfferings(prev => prev.map(x => x.id === o.id ? { ...x, tags } : x));
                                  }}
                                />
                              </div>

                              {/* ── Documents ── */}
                              <div className="md:col-span-6 border-t pt-4 mt-2">
                                <div className="flex items-center justify-between mb-2">
                                  <label className="text-xs font-semibold text-muted-foreground flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> Documents</label>
                                  <div className="flex gap-1.5">
                                    <button
                                      type="button"
                                      onClick={openLibrary}
                                      className="text-xs px-2 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700 flex items-center gap-1"
                                    >
                                      <Library className="w-3 h-3" />
                                      Add from Library
                                    </button>
                                    <button
                                      type="button"
                                      disabled={docUploading}
                                      onClick={() => docInputRef.current?.click()}
                                      className="text-xs px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1 disabled:opacity-50"
                                    >
                                      {docUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                                      Upload
                                    </button>
                                  </div>
                                  <input
                                    ref={docInputRef}
                                    type="file"
                                    multiple
                                    accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.gif,.ppt,.pptx"
                                    className="hidden"
                                    onChange={(e) => { if (e.target.files?.length) handleDocUpload(o.id, e.target.files); }}
                                  />
                                </div>

                                {/* Library picker */}
                                {showLibrary && (
                                  <div className="mb-3 border rounded-lg p-3 bg-white shadow-sm">
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="text-xs font-semibold text-indigo-700">Select from Document Library</span>
                                      <button type="button" onClick={() => setShowLibrary(false)} className="text-gray-400 hover:text-gray-600"><X className="w-3.5 h-3.5" /></button>
                                    </div>
                                    {libraryLoading ? (
                                      <div className="flex items-center justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-indigo-500" /></div>
                                    ) : libraryDocs.length === 0 ? (
                                      <p className="text-xs text-gray-400 italic py-2">No documents in library</p>
                                    ) : (
                                      <ul className="space-y-1 max-h-48 overflow-y-auto">
                                        {libraryDocs.map(ld => {
                                          const alreadyAttached = dealDocs.some(dd => dd.title === ld.title);
                                          return (
                                            <li key={ld.id} className="flex items-center gap-2 text-xs p-1.5 rounded hover:bg-indigo-50 cursor-pointer group">
                                              <FileText className="w-3.5 h-3.5 text-indigo-400 shrink-0" />
                                              <span className="truncate flex-1">{ld.title}</span>
                                              <span className="text-gray-400 text-[10px] shrink-0">{ld.folder}</span>
                                              {ld.file_size_bytes && <span className="text-gray-400 shrink-0">{fmtBytes(ld.file_size_bytes)}</span>}
                                              {alreadyAttached ? (
                                                <span className="text-[10px] text-green-600 shrink-0">Added</span>
                                              ) : (
                                                <button
                                                  type="button"
                                                  onClick={() => handleAttachDoc(o.id, ld.id)}
                                                  className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 hover:bg-indigo-200 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                                >
                                                  + Add
                                                </button>
                                              )}
                                            </li>
                                          );
                                        })}
                                      </ul>
                                    )}
                                  </div>
                                )}

                                {dealDocs.length === 0 ? (
                                  <p className="text-xs text-gray-400 italic">No documents yet</p>
                                ) : (
                                  <ul className="space-y-1 max-h-40 overflow-y-auto">
                                    {dealDocs.map((doc) => (
                                      <li key={doc.id} className="flex items-center gap-2 text-xs bg-gray-50 rounded p-1.5 group">
                                        <FileText className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                        <span className="truncate flex-1" title={doc.title || doc.filename}>{doc.title || doc.filename}</span>
                                        {doc.file_size_bytes && <span className="text-gray-400 shrink-0">{fmtBytes(doc.file_size_bytes)}</span>}
                                        <button
                                          type="button"
                                          title={doc.requires_esign ? 'Remove e-signature requirement' : 'Require e-Signature'}
                                          onClick={() => handleEsignToggle(o.id, doc.id, !!doc.requires_esign)}
                                          className={`flex items-center gap-1 px-1.5 py-0.5 rounded shrink-0 transition-colors ${doc.requires_esign ? 'bg-amber-100 text-amber-700 border border-amber-200' : 'text-gray-400 hover:text-amber-600 opacity-0 group-hover:opacity-100'}`}
                                        >
                                          <PenLine className="w-3 h-3" />
                                          {doc.requires_esign && <span>e-Sign</span>}
                                        </button>
                                        <select
                                          value={doc.visible_to || 'public'}
                                          onChange={(e) => handleVisibilityChange(o.id, doc.id, e.target.value)}
                                          className="text-[10px] px-1 py-0.5 rounded border border-gray-200 bg-white text-gray-600 shrink-0 cursor-pointer"
                                          title="Document visibility"
                                        >
                                          <option value="public">Public</option>
                                          <option value="investors">During Investment</option>
                                          <option value="holders">Holders Only</option>
                                        </select>
                                        <a href={doc.download_url} target="_blank" rel="noreferrer" className="text-blue-500 hover:text-blue-700 shrink-0"><Download className="w-3.5 h-3.5" /></a>
                                        <button type="button" onClick={() => handleDocDelete(o.id, doc.id)} className="text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>

                              {/* ── Page Content Editor ── */}
                              <div className="md:col-span-6 border-t pt-4 mt-2">
                                <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                                  <div>
                                    <p className="text-sm font-semibold text-indigo-900">Offering Page Content</p>
                                    <p className="text-xs text-indigo-800/80">Offering detail page content is now managed in Page Builder templates.</p>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => navigate('/admin/page-builder', {
                                      state: {
                                        prefill: {
                                          entityType: 'offering',
                                          entityId: o.id,
                                          title: o.title,
                                        },
                                      },
                                    })}
                                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-indigo-300 text-indigo-700 hover:bg-indigo-100 text-sm font-medium"
                                  >
                                    <Layers className="w-4 h-4" />
                                    Open Page Builder
                                  </button>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

/* ====================================================================== */
/*  Offering Tag Editor Component                                         */
/* ====================================================================== */

function OfferingTagEditor({
  offeringId,
  initialTags,
  onTagsUpdated,
}: {
  offeringId: string;
  initialTags: string[];
  onTagsUpdated: (tags: string[]) => void;
}) {
  const [tags, setTags] = React.useState<string[]>(initialTags);
  const [newTag, setNewTag] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const tagsRef = React.useRef<string[]>(initialTags);

  React.useEffect(() => { setTags(initialTags); tagsRef.current = initialTags; }, [JSON.stringify(initialTags)]);

  const saveTags = async (updated: string[]) => {
    setTags(updated);
    tagsRef.current = updated;
    setSaving(true);
    try {
      await offeringsService.adminUpdate(offeringId, { tags: updated });
      onTagsUpdated(updated);
    } catch (err: any) {
      setTags(tagsRef.current);
      toast.error(err?.message || 'Failed to update tags');
    } finally {
      setSaving(false);
    }
  };

  const addTag = () => {
    const tag = newTag.trim().toLowerCase().replace(/\s+/g, '-');
    if (!tag) { setNewTag(''); return; }
    const current = tagsRef.current;
    if (current.includes(tag)) { setNewTag(''); return; }
    saveTags([...current, tag]);
    setNewTag('');
  };

  const removeTag = (tag: string) => {
    saveTags(tagsRef.current.filter(t => t !== tag));
  };

  return (
    <div className="space-y-2">
      <label className="text-xs font-semibold text-muted-foreground">Tags</label>
      <div className="flex flex-wrap items-center gap-1.5">
        {tags.map(tag => (
          <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-medium">
            {tag}
            <button onClick={() => removeTag(tag)} className="hover:text-red-600" disabled={saving}>
              <X size={12} />
            </button>
          </span>
        ))}
        <div className="inline-flex items-center gap-1">
          <input
            type="text"
            value={newTag}
            onChange={e => setNewTag(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
            placeholder="Add tag..."
            className="h-6 w-24 px-1.5 text-xs border border-gray-200 rounded bg-gray-50 text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500"
            disabled={saving}
          />
          <button
            onClick={addTag}
            disabled={saving || !newTag.trim()}
            className="h-6 px-1.5 text-xs bg-blue-600 text-white rounded disabled:opacity-50"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminOfferings;
